import { useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimation } from 'framer-motion';

/**
 * FloatingDrawer - Premium floating panel overlay
 *
 * Features:
 * - Slides up from bottom with spring physics (tension: 300, friction: 30)
 * - Swipe-to-dismiss gesture (drag down > 100px or velocity > 500)
 * - Backdrop dim to rgba(0,0,0,0.4)
 * - Top grip handle for visual affordance
 * - Rounded top corners (16px), flush bottom
 * - Shadow: 0 -8px 24px rgba(0,0,0,0.15)
 * - Prevents scrolling of underlying content
 *
 * @param {boolean} isOpen - Whether drawer is visible
 * @param {function} onClose - Callback to close drawer
 * @param {string} height - CSS height (default: 65vh)
 * @param {ReactNode} children - Drawer content
 */
export default function FloatingDrawer({
  isOpen = false,
  onClose,
  height = '65vh',
  children
}) {
  const dragY = useMotionValue(0);
  const controls = useAnimation();

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      // Only reset if no other drawer might be open
      // Use a small delay to prevent flicker when switching drawers
      const timer = setTimeout(() => {
        if (!isOpen) {
          document.body.style.overflow = '';
          document.body.style.touchAction = '';
        }
      }, 50);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  // Handle drag end - close if dragged down enough
  const handleDragEnd = (event, info) => {
    const shouldClose = info.offset.y > 100 || info.velocity.y > 500;

    if (shouldClose) {
      onClose();
    } else {
      // Snap back to original position
      controls.start({ y: 0 });
    }
  };

  // Transform opacity based on drag position (fade out as dragged down)
  const opacity = useTransform(dragY, [0, 200], [1, 0.5]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black z-[45]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
          />

          {/* Drawer */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 bg-gjyl-dark rounded-t-2xl z-50 overflow-hidden"
            style={{
              height,
              y: dragY,
              opacity,
              boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.15)',
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
            initial={{ y: '100%', opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                type: 'spring',
                stiffness: 300,
                damping: 30,
                mass: 0.8
              }
            }}
            exit={{
              y: '100%',
              opacity: 0,
              transition: {
                type: 'spring',
                stiffness: 400,
                damping: 35
              }
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            {/* Grip handle - visual affordance for swipe */}
            <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
              <motion.div
                className="w-12 h-1 bg-gray-500 rounded-full"
                whileHover={{ scaleX: 1.2 }}
                transition={{ duration: 0.2 }}
              />
            </div>

            {/* Content */}
            <div className="h-[calc(100%-32px)] overflow-hidden">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
